


import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import LoadSpinner from "../../component/Modals/LoadSpinner";
import { Modal } from "flowbite-react";
import PaystackPop from "@paystack/inline-js";
import { useSocket } from "../../hooks/useSocket";
import { ChatIcon, PhoneIcon } from "@heroicons/react/solid";

const EmployerAcceptedRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedJobSeeker, setSelectedJobSeeker] = useState(null);
  const [messages, setMessages] = useState({});
  const [chatInput, setChatInput] = useState("");
  const [peerConnection, setPeerConnection] = useState(null);
  const [selectedChatRequestId, setSelectedChatRequestId] = useState(null); // Track chat modal
  const userId = localStorage.getItem("userId");
  const token = localStorage.getItem("token");
  const { socket } = useSocket();

  useEffect(() => {
    if (!userId || !token) {
      toast.error("Please log in to continue");
      return;
    }

    let isMounted = true;

    if (socket) {
      socket.on("acceptanceUpdate", (data) => {
        if (isMounted) {
          setRequests((prev) =>
            prev.map((r) =>
              r._id === data.requestId ? { ...r, acceptedBy: data.acceptedBy } : r
            )
          );
          toast.info("A job seeker accepted your request");
        }
      });

      socket.on("selectionUpdate", (data) => {
        if (isMounted) {
          setRequests((prev) =>
            prev.map((r) =>
              r._id === data.requestId
                ? { ...r, selectedJobSeeker: data.selectedJobSeeker, selectedJobSeekerDetails: data.selectedJobSeekerDetails, paymentStatus: "pending" }
                : r
            )
          );
        }
      });

      socket.on("paymentUpdate", (data) => {
        if (isMounted) {
          setRequests((prev) =>
            prev.map((r) =>
              r._id === data.requestId ? { ...r, status: "assigned", paymentStatus: "paid", selectedJobSeekerDetails: data.jobSeekerDetails } : r
            )
          );
          toast.success("Payment successful");
          fetchChatHistory(data.requestId);
        }
      });

      socket.on("adminAllocation", (data) => {
        if (isMounted) {
          setRequests((prev) =>
            prev.map((r) =>
              r._id === data.requestId
                ? { ...r, status: "assigned", selectedJobSeeker: data.selectedJobSeeker, selectedJobSeekerDetails: data.selectedJobSeekerDetails, allocatedBy: data.allocatedBy }
                : r
            )
          );
          toast.info("Job assigned by admin");
          fetchChatHistory(data.requestId);
        }
      });

      socket.on("requestCancelled", (data) => {
        if (isMounted) {
          setRequests((prev) => prev.filter((r) => r._id !== data.requestId));
          toast.info(data.message);
        }
      });

      socket.on("acceptanceCancelled", (data) => {
        if (isMounted) {
          setRequests((prev) =>
            prev.map((r) =>
              r._id === data.requestId
                ? { ...r, acceptedBy: r.acceptedBy.filter((a) => a.jobSeekerId !== data.jobSeekerId) }
                : r
            )
          );
          toast.info(data.message);
        }
      });

      socket.on("newMessage", (msg) => {
        if (isMounted) {
          setMessages((prev) => ({
            ...prev,
            [msg.requestId]: [...(prev[msg.requestId] || []), msg],
          }));
          toast.info(`New message in request ${msg.requestId}`);
        }
      });

      socket.on("callOffer", async (data) => {
        if (isMounted) {
          const pc = new RTCPeerConnection({ iceServers: [{ urls: "stun:stun.l.google.com:19302" }] });
          setPeerConnection(pc);
          await pc.setRemoteDescription(new RTCSessionDescription(data.offer));
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          socket.emit("callAnswer", { requestId: data.requestId, answer, calleeId: userId });

          pc.ontrack = (event) => {
            const remoteVideo = document.getElementById(`remoteVideo-${data.requestId}`);
            if (remoteVideo) remoteVideo.srcObject = event.streams[0];
          };

          pc.onicecandidate = (event) => {
            if (event.candidate) {
              socket.emit("iceCandidate", { requestId: data.requestId, candidate: event.candidate, senderId: userId });
            }
          };

          const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
          stream.getTracks().forEach((track) => pc.addTrack(track, stream));
          const localVideo = document.getElementById(`localVideo-${data.requestId}`);
          if (localVideo) localVideo.srcObject = stream;

          toast.info(`Incoming call for request ${data.requestId}`);
        }
      });

      socket.on("callAnswer", async (data) => {
        if (isMounted && peerConnection) {
          await peerConnection.setRemoteDescription(new RTCSessionDescription(data.answer));
        }
      });

      socket.on("iceCandidate", async (data) => {
        if (isMounted && peerConnection) {
          await peerConnection.addIceCandidate(new RTCIceCandidate(data.candidate));
        }
      });
    }

    const fetchRequests = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}urgent/accepted-requests/${userId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (isMounted) {
          setRequests(
            response.data.requests.map((r) => ({
              ...r,
              acceptedBy: r.acceptedBy.map((a) => ({
                ...a.jobSeekerId,
                travelTime: a.travelTime,
              })),
              selectedJobSeekerDetails: r.selectedJobSeeker ? r.selectedJobSeeker : null,
              paymentStatus: r.transactionRef ? "paid" : "pending",
            }))
          );
        }
      } catch (error) {
        if (isMounted) {
          toast.error(error.response?.data?.message || "Failed to fetch accepted requests");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    fetchRequests();

    return () => {
      isMounted = false;
      if (socket) {
        socket.off("acceptanceUpdate");
        socket.off("selectionUpdate");
        socket.off("paymentUpdate");
        socket.off("adminAllocation");
        socket.off("requestCancelled");
        socket.off("acceptanceCancelled");
        socket.off("newMessage");
        socket.off("callOffer");
        socket.off("callAnswer");
        socket.off("iceCandidate");
      }
    };
  }, [userId, token, socket]);

  const fetchChatHistory = async (requestId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}urgent/chat-history/${requestId}/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages((prev) => ({ ...prev, [requestId]: response.data.messages }));
    } catch (error) {
      toast.error("Failed to fetch chat history");
    }
  };

  const handleSelect = async (requestId, jobSeekerId) => {
    if (!userId || !token) {
      toast.error("Please log in to continue");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}urgent/select-jobseeker-and-pay`,
        { requestId, jobSeekerId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const paystack = new PaystackPop();
      paystack.newTransaction({
        key: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY,
        email: response.data.email,
        amount: response.data.amount * 100,
        reference: response.data.transactionRef,
        callback: async (res) => {
          if (res.status === "success") {
            try {
              // Notify backend of successful payment
              await axios.post(
                `${process.env.REACT_APP_API_URL}urgent/confirm-payment`,
                { requestId, transactionRef: res.reference },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              setRequests((prev) =>
                prev.map((r) =>
                  r._id === requestId
                    ? { ...r, status: "assigned", paymentStatus: "paid", selectedJobSeeker: jobSeekerId }
                    : r
                )
              );
              toast.success("Payment successful");
              fetchChatHistory(requestId);
            } catch (error) {
              toast.error(error.response?.data?.message || "Failed to confirm payment");
            }
          } else {
            toast.error("Payment failed");
          }
          setLoading(false);
        },
        onClose: () => {
          toast.info("Payment popup closed");
          setLoading(false);
        },
      });
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to select job seeker");
      setLoading(false);
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (!userId || !token) {
      toast.error("Please log in to continue");
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}urgent/cancel-request`,
        { requestId, userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Request cancelled");
      setRequests((prev) => prev.filter((r) => r._id !== requestId));
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to cancel request");
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = (requestId) => {
    if (!chatInput) {
      toast.error("Please enter a message");
      return;
    }
    socket.emit("sendMessage", { requestId, senderId: userId, message: chatInput });
    setMessages((prev) => ({
      ...prev,
      [requestId]: [...(prev[requestId] || []), { senderId: userId, message: chatInput, timestamp: new Date() }],
    }));
    setChatInput("");
  };

  const startCall = async (requestId) => {
    try {
      const pc = new RTCPeerConnection({ iceServers: [{ urls: "stun:stun.l.google.com:19302" }] });
      setPeerConnection(pc);

      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      const localVideo = document.getElementById(`localVideo-${requestId}`);
      if (localVideo) localVideo.srcObject = stream;
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          socket.emit("iceCandidate", { requestId, candidate: event.candidate, senderId: userId });
        }
      };

      pc.ontrack = (event) => {
        const remoteVideo = document.getElementById(`remoteVideo-${requestId}`);
        if (remoteVideo) remoteVideo.srcObject = event.streams[0];
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      socket.emit("callOffer", { requestId, offer, callerId: userId });
      toast.info("Initiating video call...");
    } catch (error) {
      toast.error("Failed to start call");
      console.error(error);
    }
  };

  const makePhoneCall = (phoneNumber) => {
    const link = document.createElement("a");
    link.href = `tel:${phoneNumber}`;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.info("Initiating phone call...");
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Accepted Urgent Requests</h2>
      {loading && <LoadSpinner />}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {requests.map((request) => (
          <div
            key={request._id}
            className="bg-white rounded-lg shadow-md p-6 relative hover:shadow-lg transition-shadow"
          >
            <h3 className="text-lg font-semibold text-gray-800">{request.position}</h3>
            <p className="text-gray-600">Duration: {request.durationValue} {request.durationType}</p>
            <p className="text-gray-600">Amount: N{request.amount}</p>
            {request.allocatedBy && (
              <p className="text-gray-600">
                <span className="font-medium">Allocated By:</span> {request.allocatedBy.fullName} ({request.allocatedBy.email})
              </p>
            )}
            {request.acceptedBy.length > 0 && (
              <div className="mt-4">
                <h4 className="text-md font-semibold text-gray-800">Accepted By:</h4>
                {request.acceptedBy.map((jobSeeker) => (
                  <div key={jobSeeker._id} className="mt-2 border-t pt-2">
                    <p className="text-gray-600">
                      <span className="font-medium">Name:</span> {jobSeeker.firstName} {jobSeeker.lastName}
                    </p>
                    <p className="text-gray-600"><span className="font-medium">Email:</span> {jobSeeker.email}</p>
                    <p className="text-gray-600"><span className="font-medium">Phone:</span> {jobSeeker.phoneNumber}</p>
                    <p className="text-gray-600"><span className="font-medium">Travel Time:</span> {jobSeeker.travelTime}</p>
                    {!request.selectedJobSeeker && !request.allocatedBy && (
                      <div className="mt-2 flex space-x-2">
                        <button
                          onClick={() => handleSelect(request._id, jobSeeker._id)}
                          className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition disabled:bg-blue-400"
                          disabled={loading}
                        >
                          {loading ? <LoadSpinner /> : "Select & Pay"}
                        </button>
                        <button
                          onClick={() => setSelectedJobSeeker(jobSeeker)}
                          className="bg-gray-500 text-white py-2 px-4 rounded-md hover:bg-gray-600 transition"
                        >
                          View Details
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {request.selectedJobSeekerDetails && (
              <div className="mt-4 border-t pt-2">
                <h4 className="text-md font-semibold text-gray-800">Selected Job Seeker:</h4>
                <p className="text-gray-600">
                  <span className="font-medium">Name:</span> {request.selectedJobSeekerDetails.firstName} {request.selectedJobSeekerDetails.lastName}
                </p>
                <p className="text-gray-600"><span className="font-medium">Email:</span> {request.selectedJobSeekerDetails.email}</p>
                <p className="text-gray-600"><span className="font-medium">Phone:</span> {request.selectedJobSeekerDetails.phoneNumber}</p>
                <p className="text-gray-600">
                  <span className="font-medium">Travel Time:</span>{" "}
                  {request.acceptedBy.find((a) => a._id === request.selectedJobSeeker)?.travelTime}
                </p>
                {request.paymentStatus === "paid" && request.status === "assigned" ? (
                  <div className="mt-2 flex space-x-2">
                    <button
                      onClick={() => setSelectedChatRequestId(request._id)}
                      className="flex items-center bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition"
                    >
                      <ChatIcon className="w-5 h-5 mr-2" />
                      Chat
                    </button>
                    <button
                      onClick={() => startCall(request._id)}
                      className="flex items-center bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition"
                    >
                      <PhoneIcon className="w-5 h-5 mr-2" />
                      Video Call
                    </button>
                    <button
                      onClick={() => makePhoneCall(request.selectedJobSeekerDetails.phoneNumber)}
                      className="flex items-center bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700 transition"
                    >
                      <PhoneIcon className="w-5 h-5 mr-2" />
                      Phone Call
                    </button>
                  </div>
                ) : (
                  <p className="text-gray-600 mt-2">Payment Pending</p>
                )}
              </div>
            )}
            {request.acceptedBy.length === 0 && !request.selectedJobSeeker && (
              <p className="text-gray-600 mt-2">No acceptances yet.</p>
            )}
            {request.status === "pending" && !request.selectedJobSeeker && (
              <button
                onClick={() => handleCancelRequest(request._id)}
                className="mt-4 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition disabled:bg-red-400"
                disabled={loading}
              >
                {loading ? <LoadSpinner /> : "Cancel Request"}
              </button>
            )}
            {request.status === "assigned" && request.paymentStatus === "paid" && (
              <div className="mt-4 flex space-x-4">
                <video id={`localVideo-${request._id}`} autoPlay playsInline className="w-40 h-40 rounded-md border"></video>
                <video id={`remoteVideo-${request._id}`} autoPlay playsInline className="w-40 h-40 rounded-md border"></video>
              </div>
            )}
          </div>
        ))}
      </div>
      {selectedJobSeeker && (
        <Modal show={!!selectedJobSeeker} onClose={() => setSelectedJobSeeker(null)}>
          <Modal.Header>
            <h3 className="text-lg font-semibold text-gray-800">Job Seeker Details</h3>
          </Modal.Header>
          <Modal.Body>
            <div className="space-y-2">
              <p><span className="font-medium">Name:</span> {selectedJobSeeker.firstName} {selectedJobSeeker.lastName}</p>
              <p><span className="font-medium">Email:</span> {selectedJobSeeker.email}</p>
              <p><span className="font-medium">Phone:</span> {selectedJobSeeker.phoneNumber}</p>
              <p><span className="font-medium">Address:</span> {selectedJobSeeker.address}</p>
              <p><span className="font-medium">Travel Time:</span> {selectedJobSeeker.travelTime}</p>
              <p><span className="font-medium">Job Decision Salary:</span> {selectedJobSeeker.jobDecisionSalary}</p>
              <p><span className="font-medium">Minimum Salary:</span> {selectedJobSeeker.minimumSalary}</p>
              <p><span className="font-medium">Interested Jobs:</span> {selectedJobSeeker.interestedJobs?.join(", ") || "N/A"}</p>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              onClick={() => setSelectedJobSeeker(null)}
              className="bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition"
            >
              Close
            </button>
          </Modal.Footer>
        </Modal>
      )}
      {selectedChatRequestId && (
        <Modal show={!!selectedChatRequestId} onClose={() => setSelectedChatRequestId(null)}>
          <Modal.Header>
            <h3 className="text-lg font-semibold text-gray-800">Chat with Job Seeker</h3>
          </Modal.Header>
          <Modal.Body>
            <div className="border border-gray-200 rounded-md p-2 h-64 overflow-y-auto bg-gray-50">
              {(messages[selectedChatRequestId] || []).map((msg, idx) => (
                <p
                  key={idx}
                  className={`text-sm ${msg.senderId === userId ? "text-right text-blue-600" : "text-left text-gray-600"} mb-2`}
                >
                  <span className="font-medium">{msg.senderId === userId ? "You" : "Job Seeker"}:</span> {msg.message}
                  <span className="block text-xs text-gray-400">
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </span>
                </p>
              ))}
            </div>
            <div className="mt-4 flex space-x-2">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Type a message..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={() => sendMessage(selectedChatRequestId)}
                className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition"
              >
                Send
              </button>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              onClick={() => setSelectedChatRequestId(null)}
              className="bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition"
            >
              Close
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default EmployerAcceptedRequests;